/**
 * Custom implementation of candlestick series.
 * The one from ECharts has limited features and lack hollow candles.
 */

import α from 'color-alpha';
import * as echarts from 'echarts';

const makeRect = (
  mid: number,
  top: number,
  bottom: number,
  width: number,
  fill: string,
  stroke: string,
  clipRect: echarts.ERectangle
): echarts.EChartOption.SeriesCustom.RenderItemReturnRect => ({
  type: 'rect',
  shape: echarts.graphic.clipRectByRect(
    {
      x: mid - width / 2,
      y: top,
      width,
      height: bottom - top,
    },
    clipRect
  ),
  style: {
    fill,
    stroke,
  },
});

/**
 * makeRenderItem returns a function "makeRenderItem"
 * renderItem will draw three rectangles:
 * - candlestick's box
 * - candlestick's top stick (needle)
 * - candlestick's bottom stick (needle)
 */
const makeRenderItem = (
  bullishColor: string,
  bearishColor: string
): echarts.EChartOption.SeriesCustom.RenderItem => {
  return (params, api) => {
    const canvasRect: echarts.ERectangle = {
      x: params.coordSys.x,
      y: params.coordSys.y,
      width: params.coordSys.width,
      height: params.coordSys.height,
    };

    const dateIndex = api.value(0);
    const open = api.value(1);
    const close = api.value(2);
    const low = api.value(3);
    const high = api.value(4);
    const prevClose = api.value(5);

    const pointOpen = api.coord([dateIndex, open]);
    const pointClose = api.coord([dateIndex, close]);
    const pointLow = api.coord([dateIndex, low]);
    const pointHigh = api.coord([dateIndex, high]);

    const boxWidth = api.size([1, 1])[0] * 0.5;
    const boxMiddle = pointClose[0];

    const [boxTop, boxBottom] =
      pointClose[1] > pointOpen[1]
        ? [pointOpen[1], pointClose[1]]
        : [pointClose[1], pointOpen[1]];

    const needleWidth = boxWidth * 0.2;
    const needleMiddle = boxMiddle;
    const needleTop = pointHigh[1];
    const needleBottom = pointLow[1];

    const color = close > (prevClose || open) ? bullishColor : bearishColor;

    return {
      type: 'group',
      children: [
        // the box part
        makeRect(
          boxMiddle,
          boxTop,
          boxBottom,
          boxWidth,
          // close > open => hollow, else it is solid
          close > open ? 'transparent' : α(color, 0.7),
          color,
          canvasRect
        ),
        // the top stick (needle)
        makeRect(
          needleMiddle,
          needleTop,
          boxTop,
          needleWidth,
          color,
          color,
          canvasRect
        ),
        // the bottom stick (needle)
        makeRect(
          needleMiddle,
          boxBottom,
          needleBottom,
          needleWidth,
          color,
          color,
          canvasRect
        ),
      ],
    };
  };
};

export const makeCandleStockSeries = (
  bullishColor: string,
  bearishColor: string
): echarts.EChartOption.SeriesCustom => {
  return {
    name: 'Prices',
    type: 'custom',
    dimensions: ['date', 'open', 'close', 'low', 'high', 'previousClose'],
    renderItem: makeRenderItem(bullishColor, bearishColor),
    // make y-axis range to respect all required dimensions
    // i.e. 'open', 'close', 'low', 'high' and 'previousClose'
    // https://echarts.apache.org/examples/zh/editor.html?c=custom-ohlc
    encode: {
      y: [1, 2, 3, 4, 5],
    },
  };
};
